<template>
  <ak-sidebar-layout size="huge" sidebar-style="light">
    <template #sidebar>
      <ak-box class="pb-large overflow-y">
        <h3 class="color-grey mb-small">Create Survey</h3>
        <ak-survey-form
          ref="akSurveyForm"
          v-model:name="survey.name"
          v-model:slug="survey.slug"
          v-model:description="survey.description"
          v-model:survey-url="survey.survey_url"
          v-model:start-date="survey.start_date"
          v-model:end-date="survey.end_date"
          v-model:completion-limit="survey.completion_limit.value"
          v-model:credit="survey.credit.value"
          v-model:access="survey.access"
          v-model:status="survey.status"
          :account-id="activeAccount._id"
          :agency-id="activeAgency._id"
        />
      </ak-box>
    </template>
    <template #body>
      <ak-tabs v-model="activeTab" class="h-full">
        <template #navRight>
          <ak-button
            class="mt-tiny mr-small"
            color="success"
            @click.stop.prevent="create"
          >
            Create
          </ak-button>
        </template>
        <ak-tab-item label="Preview">
          <preview-iframe :survey-url="survey.survey_url" />
        </ak-tab-item>
      </ak-tabs>
    </template>
  </ak-sidebar-layout>
</template>

<script>
import { ref, defineComponent, computed } from 'vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import SidebarLayout from '@akerolabs/akero-vue/src/components/sidebar/SidebarLayout.vue'
import SurveyForm from './SurveyForm.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Tabs from '@akerolabs/akero-vue/src/components/tabs/Tabs.vue'
import TabItem from '@akerolabs/akero-vue/src/components/tabs/TabItem.vue'
import PreviewIframe from './tabs/PreviewIframe.vue'
import { formatDate } from '@akerolabs/akero-vue/src/filters/date'
import moment from '@akerolabs/akero-vue/src/utils/date'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { success, error } from './utils'

export default defineComponent({
  name: 'AkNewSurvey',
  components: {
    [SidebarLayout.name]: SidebarLayout,
    [Box.name]: Box,
    [SurveyForm.name]: SurveyForm,
    [Button.name]: Button,
    [Tabs.name]: Tabs,
    [TabItem.name]: TabItem,
    [PreviewIframe.name]: PreviewIframe,
  },
  inheritAttrs: false,
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },
    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },
    /**
     * @type {Object}
     * @description The users profile data
     */
    userProfile: {
      default: () => ({}),
      type: Object,
    },
  },
  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props) {
    const now = Date.now()
    const akSurveyForm = ref(null)
    const activeTab = ref(0)
    const survey = ref({
      name: undefined,
      slug: undefined,
      description: undefined,
      survey_url: undefined,
      start_date: formatDate(now, 'DD/MM/YYYY'),
      end_date: formatDate(moment(now).add(1, 'months').toDate(), 'DD/MM/YYYY'),
      completion_limit: {
        value: undefined,
        enabled: false,
      },
      credit: {
        enabled: true,
        value: 50,
      },
      access: 'open',
      status: 'unpublished',
    })

    /**
     * @description get an api ready set of data
     * @type {Object}
     */
    const newSurvey = computed(() => {
      const newSurveyObj = Object.assign(
        {
          survey_type: 'survey',
          current_completion_value: 0,
          survey_source: 'survey-gizmo-url',
          disqualified: false,
          deleted: false,
          account: props.activeAccount._id,
          agency: props.activeAgency._id,
          createdBy: props.userProfile._id,
          createdByName: props.userProfile.name,
        },
        { ...survey.value },
      )
      if (newSurveyObj.completion_limit.value > 0) {
        newSurveyObj.completion_limit.enabled = true
      }
      newSurveyObj.start_date = dbReadyDate(newSurveyObj.start_date, 'start')
      newSurveyObj.end_date = dbReadyDate(newSurveyObj.end_date, 'end')
      return newSurveyObj
    })

    /**
     * @description Make and api request to create a new survey
     */
    const create = async () => {
      const isValid = await akSurveyForm.value.validate()
      if (isValid) {
        const createSurvey = { ...newSurvey.value }
        try {
          const { data } = await request.post('/survey', createSurvey)
          if (data.status === 'success') {
            success('Survey created')
          }
        } catch (ex) {
          error('Survey not created', ex)
        }
      } else {
        error('Please fix validation errors')
      }
    }

    /**
     * @description Get an iso date ready for the database
     * @param {String} date
     * @param {String} time
     */
    const dbReadyDate = (date, time) => {
      if (time === 'start') {
        return moment(date, 'DD/MM/YYYY').startOf('day').toISOString()
      }
      if (time === 'end') {
        return moment(date, 'DD/MM/YYYY').endOf('day').toISOString()
      }
      return null
    }

    return {
      akSurveyForm,
      survey,
      activeTab,
      create,
    }
  },
})
</script>
